/**
 * Google Tag Manager DataLayer Tracking Utilities
 *
 * All functions push events to window.dataLayer for GTM tracking.
 * Prices are always numbers without currency symbols.
 * Currency is always "USD".
 */

// Ensure dataLayer exists
if (typeof window !== 'undefined' && !window.dataLayer) {
  window.dataLayer = [];
}

/**
 * Push event to dataLayer
 * @param {Object} event - Event object to push
 */
const pushToDataLayer = (event) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
    // console.log('[DataLayer] Event pushed:', event);
  }
};

/**
 * Track form start when user enters postal code
 *
 * Example:
 * trackFormStart('94203', true, franchise)
 *
 * @param {string} postalCode - User's postal code
 * @param {boolean} serviceable - Whether location is serviceable
 * @param {Object} franchise - Franchise object (optional if not serviceable)
 */
export const trackFormStart = (postalCode, serviceable, franchise = null) => {
  pushToDataLayer({
    event: 'schedule_form_start',
    postal_code: postalCode,
    serviceable: serviceable,
    service_category: 'home',
    franchise_location: franchise?.franchise || null
  });
};

/**
 * Track service selection
 *
 * Example:
 * trackSelectService(
 *   ['Air Duct Cleaning', 'Carpet Cleaning', 'Drapery Cleaning'],
 *   ['Carpet Cleaning', 'Area Rug Cleaning']
 * )
 *
 * @param {Array<string>} availableServices - All available service names
 * @param {Array<string>} selectedServices - Selected service names
 */
export const trackSelectService = (availableServices, selectedServices) => {
  pushToDataLayer({
    event: 'select_service',
    service_category: 'home',
    available_services: availableServices,
    selected_services: selectedServices,
    multi_service: selectedServices.length > 1
  });
};

/**
 * Track service details configuration
 *
 * Example:
 * trackServiceDetails([
 *   { service_type: 'Carpet Cleaning', details: ['clean', 'protect'] },
 *   { service_type: 'Area Rug Cleaning', details: ['clean', 'deodorize'] }
 * ], 678)
 *
 * @param {Array<{service_type: string, details: Array<string>}>} services - Services with their detail selections
 * @param {number} estimateTotal - Total estimate amount
 */
export const trackServiceDetails = (services, estimateTotal) => {
  pushToDataLayer({
    event: 'service_details',
    service_category: 'home',
    services: services,
    estimate_total: estimateTotal,
    currency: 'USD'
  });
};

/**
 * Track appointment scheduling
 *
 * Example:
 * trackScheduleServices([
 *   { date: '2026-03-09', arrival_window: 'AVAILABLE ALL DAY', services: ['Area Rug Cleaning', 'Carpet Cleaning'] },
 *   { date: '2026-03-10', arrival_window: 'AFTERNOON APPT', services: ['Air Duct Cleaning'] }
 * ], 678, false)
 *
 * @param {Array<{date: string, arrival_window: string, services: Array<string>}>} appointments - Array of appointments with date, window, and services
 * @param {number} finalEstimate - Final estimate amount
 * @param {boolean} promoApplied - Whether promo code was applied
 */
export const trackScheduleServices = (appointments, finalEstimate, promoApplied) => {
  pushToDataLayer({
    event: 'schedule_services',
    service_category: 'home',
    appointments: appointments,
    final_estimate: finalEstimate,
    currency: 'USD',
    promo_applied: promoApplied
  });
};

/**
 * Track user information submission
 *
 * Example:
 * trackYourInformation(true, true)
 *
 * @param {boolean} emailOptIn - Email opt-in checkbox state
 * @param {boolean} smsOptIn - SMS opt-in checkbox state
 */
export const trackYourInformation = (emailOptIn, smsOptIn) => {
  pushToDataLayer({
    event: 'your_information',
    service_category: 'home',
    email_opt_in_checked: emailOptIn,
    sms_opt_in_checked: smsOptIn
  });
};

/**
 * Track final appointment confirmation
 *
 * Example:
 * trackScheduleAppointment(678, false)
 *
 * @param {number} finalEstimate - Final estimate amount
 * @param {boolean} promoApplied - Whether promo code was applied
 */
export const trackScheduleAppointment = (finalEstimate, promoApplied) => {
  pushToDataLayer({
    event: 'schedule_appointment',
    service_category: 'home',
    final_estimate: finalEstimate,
    currency: 'USD',
    promo_applied: promoApplied
  });
};

/**
 * Helper: Check if any promos are applied
 * @param {Array} appliedPromotions - Applied promotion codes
 * @param {Array} manuallyEnteredPromoCodes - Manually entered promo codes
 * @returns {boolean}
 */
export const hasPromoApplied = (appliedPromotions, manuallyEnteredPromoCodes) => {
  return appliedPromotions.length > 0 || manuallyEnteredPromoCodes.length > 0;
};

/**
 * Helper: Extract service names from service objects
 * @param {Array<Object>} services - Array of service objects
 * @returns {Array<string>} Array of service names
 */
export const extractServiceNames = (services) => {
  if (!Array.isArray(services)) return [];
  return services.map(service => {
    if (typeof service === 'string') return service;
    return service.title || service.name || service.service_name || '';
  }).filter(Boolean);
};

/**
 * Helper: Format date to YYYY-MM-DD
 * @param {Date|string} date - Date object or string
 * @returns {string} Formatted date string
 */
export const formatDateForTracking = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Helper: Format arrival window from appointment object
 * @param {Object} appointment - Appointment object with time_slot
 * @returns {string} Formatted arrival window (e.g., "8am-12pm")
 */
export const formatArrivalWindow = (appointment) => {
  if (!appointment?.time_slot) return '';
  const { start, end } = appointment.time_slot;

  const formatTime = (time) => {
    const hour = parseInt(time.split(':')[0]);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}${ampm}`;
  };

  return `${formatTime(start)}-${formatTime(end)}`;
};
